import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { getBalance, claimDailyIncome } from '../api/currency';
import apiClient from '../api/client';

const TokenContext = createContext(null);

export function TokenProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [tokens, setTokens] = useState(() => {
    // Read from dashboard init cache for instant render
    try {
      const cached = sessionStorage.getItem('global_currency');
      if (cached) return JSON.parse(cached).balance || 0;
    } catch {}
    return 0;
  });
  const [animating, setAnimating] = useState(false);
  const pollRef = useRef(null);

  // Fetch balance from currency API on mount and every 60s
  const fetchBalance = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await getBalance();
      setTokens(data.balance);
    } catch (err) {
      // Silently fail — will retry on next poll
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // Delay initial fetch if dashboard init cache exists — it already
    // provided the balance, so defer the refresh to avoid tunnel contention.
    const cached = sessionStorage.getItem('global_currency');
    const delay = cached ? 3000 : 0;
    const timeout = setTimeout(() => fetchBalance(), delay);
    pollRef.current = setInterval(fetchBalance, 60000);
    return () => { clearTimeout(timeout); clearInterval(pollRef.current); };
  }, [fetchBalance]);

  // Also try to claim daily income on first load (idempotent)
  useEffect(() => {
    if (!isAuthenticated) return;
    claimDailyIncome().catch(() => {});
  }, [isAuthenticated]);

  const addToken = useCallback(async (amount = 1) => {

    // Optimistically update displayed balance immediately
    setTokens(prev => prev + amount);

    // Trigger pop animation for visual feedback
    setAnimating(true);
    setTimeout(() => setAnimating(false), 600);

    try {
      // Credit the currency system (same system that getBalance reads)
      // _skipAuthRedirect prevents 401 from logging user out for this non-critical call
      const res = await apiClient.post(
        `/api/currency/earn?amount=${amount}`,
        null,
        { _skipAuthRedirect: true }
      );
      // Use server-returned balance as authoritative value
      if (res.data?.balance != null) {
        setTokens(res.data.balance);
      }
    } catch (err) {
      // Don't revert optimistic update — next poll will sync.
      // This keeps the UI responsive even if the earn call fails transiently.
      console.warn('Currency earn failed, will sync on next poll:', err?.message);
    }
  }, []);

  // Optimistic display bump — instant visual update + pop animation without calling
  // the earn endpoint. Use when the backend already credited tokens (e.g. hint discovery).
  const bumpDisplay = useCallback((amount) => {
    setTokens(prev => prev + amount);
    setAnimating(true);
    setTimeout(() => setAnimating(false), 600);
  }, []);

  const value = useMemo(() => ({
    tokens,
    addToken,
    bumpDisplay,
    animating,
    refetchBalance: fetchBalance,
  }), [tokens, addToken, bumpDisplay, animating, fetchBalance]);

  return (
    <TokenContext.Provider value={value}>
      {children}
    </TokenContext.Provider>
  );
}

export function useTokens() {
  const context = useContext(TokenContext);
  if (!context) {
    throw new Error('useTokens must be used within TokenProvider');
  }
  return context;
}
